import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import FeatureGate from './components/subscription/FeatureGate';
import { NAV } from './constants/nav';
import en from './locales/en.json';
import hi from './locales/hi.json';
import gu from './locales/gu.json';
import { initBackButtonManager, handleRootExit, hasOpenModals, closeTopModal } from './utils/backButtonManager';
import { LoginScreen, SignupScreen, ForgotPasswordScreen, ImpersonateScreen } from './pages/auth';
import DashboardScreen from './pages/dashboard/DashboardScreen';
import { MaintenanceScreen, PettyCashScreen, BankScreen, QuotationsScreen, FestivalScreen, VendorsScreen } from './pages/finance';
import { TodosScreen, AmenitiesScreen } from './pages/operations';
import VisitorServiceProviders from './pages/operations/VisitorServiceProviders';
import SecurityGateSearch from './pages/operations/SecurityGateSearch';
import AdminServiceProviders from './pages/operations/AdminServiceProviders';
import { NoticesScreen, DocumentsScreen, ServicesScreen, SharedDocumentViewer } from './pages/documents';
import { NotificationsScreen, UsersScreen, BillingScreen, AuditLogScreen } from './pages/admin';
import { SalaryScreen, ElectionScreen, CommitteeScreen } from './pages/hr';
import { MyProfileScreen, SettingsScreen, SocietySettingsScreen, FireAlertScreen, PrivacyPolicyScreen, CreateSocietyScreen } from './pages/settings';
import { Sidebar, Header } from './components/layout';
import { LogoutModal } from './components/shared';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useSettings } from './context/SettingsContext';
import { Toaster } from 'react-hot-toast';
import { IonApp, IonPage, IonContent, IonRefresher, IonRefresherContent } from '@ionic/react';
import { Modal } from './components/ui';
import { syncDeviceToken } from './utils/capacitorPush';

const translations = { en, hi, gu };

const SCREENS = {
  dashboard: { title: "Dashboard", C: DashboardScreen },
  notifications: { title: "Notifications", C: NotificationsScreen },
  maintenance: { title: "Maintenance", C: MaintenanceScreen },
  "petty-cash": { title: "Petty Cash", C: PettyCashScreen },
  bank: { title: "Bank Accounts", C: BankScreen },
  quotations: { title: "Quotations & POs", C: QuotationsScreen },
  festival: { title: "Festival Accounts", C: FestivalScreen },
  vendors: { title: "Vendors & AMC", C: VendorsScreen },
  todos: { title: "To-Do Tasks", C: TodosScreen },
  amenities: { title: "Amenities", C: AmenitiesScreen },
  "service-providers": { title: "My Service Providers", C: VisitorServiceProviders },
  "gate-search": { title: "Security Gate Entry", C: SecurityGateSearch },
  "admin-providers": { title: "All Service Providers", C: AdminServiceProviders },
  notices: { title: "Notices & Docs", C: NoticesScreen },
  documents: { title: "Document Mgmt", C: DocumentsScreen },
  services: { title: "Service Directory", C: ServicesScreen },
  salary: { title: "Salary & Payroll", C: SalaryScreen },
  election: { title: "Election Mgmt", C: ElectionScreen },
  committee: { title: "Committee Management", C: CommitteeScreen },
  users: { title: "User Management", C: UsersScreen },
  billing: { title: "Billing & Plans", C: BillingScreen },
  audit: { title: "Audit Log", C: AuditLogScreen },
  "my-profile": { title: "My Profile", C: MyProfileScreen },
  settings: { title: "Settings", C: SettingsScreen },
  "society-settings": { title: "Society Settings", C: SocietySettingsScreen },
  "fire-alert": { title: "Emergency Alert System", C: FireAlertScreen },
  "privacy-policy": { title: "Privacy Policy", C: PrivacyPolicyScreen },
  "create-society": { title: "Create Society", C: CreateSocietyScreen },
};

function AppContent() {
  const { isAuthenticated, loading, logout, user } = useAuth();
  const { loadPreferences, loadSecuritySettings, security, recordSecurityActivity, language = 'en' } = useSettings();
  const [authState, setAuthState] = useState("login");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const isMember = user?.primary_role_label === 'Member' || user?.roles_list?.includes('member');
  const rootScreen = isMember ? 'maintenance' : 'dashboard';
  const [screen, setScreen] = useState(rootScreen);
  const [screenHistory, setScreenHistory] = useState([rootScreen]);
  const [collapsed, setCollapsed] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavOpenRef = useRef(mobileNavOpen);
  useEffect(() => {
    mobileNavOpenRef.current = mobileNavOpen;
  }, [mobileNavOpen]);

  const [refreshKey, setRefreshKey] = useState(0);
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const lastPingRef = useRef(0);

  // Read initial screen/auth state from URL on mount
  useEffect(() => {
    const path = window.location.pathname.replace('/', '');

    // Auth routes handling
    if (path === 'signup') setAuthState('signup');
    else if (path === 'forgot-password') setAuthState('forgot');
    else if (path === 'impersonate') setAuthState('impersonate');
    else if (path === 'login' || !path) setAuthState('login');

    // App screens handling
    if (path && SCREENS[path]) {
      setScreen(path);
      setScreenHistory([rootScreen, path]);
    } else {
      setScreen(rootScreen);
      setScreenHistory([rootScreen]);
    }

    const handlePopState = () => {
      // If modal is open, close modal and don't navigate page
      if (hasOpenModals()) {
        closeTopModal();
        return;
      }

      // If mobile drawer is open, close it
      if (mobileNavOpenRef.current) {
        setMobileNavOpen(false);
        return;
      }

      const p = window.location.pathname.replace('/', '');

      // Sync auth state
      if (p === 'signup') setAuthState('signup');
      else if (p === 'forgot-password') setAuthState('forgot');
      else if (p === 'impersonate') setAuthState('impersonate');
      else if (p === 'login' || !p) setAuthState('login');

      // Sync screen state
      if (p && SCREENS[p]) {
        setScreen(p);
        setScreenHistory(prev => {
          if (prev[prev.length - 1] === p) return prev;
          return [...prev, p];
        });
      } else {
        setScreen(rootScreen);
        setScreenHistory([rootScreen]);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [rootScreen]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSessionWarningOpen(false);
      setSecondsRemaining(null);
      return;
    }

    lastActivityRef.current = Date.now();
    loadSecuritySettings().catch(() => { });
  }, [isAuthenticated, loadSecuritySettings]);

  // Sync URL with authentication state and current screen/authState
  useEffect(() => {
    if (loading) return; // Wait for initial auth check to complete

    if (window.location.pathname.startsWith('/shared-doc/')) {
      return;
    }

    if (isAuthenticated) {
      if (isMember && screen === 'dashboard') {
        setScreen('maintenance');
        return;
      }
      
      const path = `/${screen}`;
      if (window.location.pathname !== path) {
        window.history.pushState({}, '', path);
      }
    } else {
      // Only sync to login/signup/forgot if we are sure we are not logged in
      let path = '/login';
      if (authState === 'signup') path = '/signup';
      else if (authState === 'forgot') path = '/forgot-password';
      else if (authState === 'impersonate') path = '/impersonate';

      if (window.location.pathname !== path) {
        window.history.pushState({}, '', path);
      }
    }
  }, [isAuthenticated, screen, authState, loading, isMember]);

  // Update browser tab title: "<Page Name> | The Clover App"
  useEffect(() => {
    let pageTitle = '';

    if (window.location.pathname.startsWith('/shared-doc/')) {
      pageTitle = 'Shared Document';
    } else if (!isAuthenticated) {
      if (authState === 'signup') pageTitle = 'Sign Up';
      else if (authState === 'forgot') pageTitle = 'Forgot Password';
      else if (authState === 'impersonate') pageTitle = 'Impersonate';
      else pageTitle = 'Login';
    } else {
      pageTitle = SCREENS[screen]?.title || (screen ? screen.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') : 'Dashboard');
    }

    document.title = pageTitle ? `${pageTitle} | The Clover App` : 'The Clover App';
  }, [isAuthenticated, screen, authState]);

  // Redirect to tenant domain if already logged in and on central domain
  useEffect(() => {
    // Skip redirection for mobile apps (Capacitor)
    const isMobileApp = window.Capacitor || window.location.protocol === 'capacitor:';
    if (isMobileApp) return;

    if (isAuthenticated && user?.domain?.domain) {
      const currentHost = window.location.hostname;
      const tenantHost = user.domain.domain;

      // If we are on central domain but have a tenant domain, redirect
      const centralDomains = ['127.0.0.1', 'localhost', 'the-society.net'];
      if (centralDomains.includes(currentHost) && currentHost !== tenantHost && !currentHost.includes('localhost')) {
        window.location.href = `${window.location.protocol}//${tenantHost}`;
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPreferences().catch(() => { });
      syncDeviceToken().catch(() => { });
    }
  }, [isAuthenticated, loadPreferences]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const markActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      setSessionWarningOpen(false);

      if (now - lastPingRef.current >= 30000) {
        lastPingRef.current = now;
        recordSecurityActivity({ activity_at: new Date(now).toISOString() }).catch(() => { });
      }
    };

    activityEvents.forEach(eventName => window.addEventListener(eventName, markActivity, { passive: true }));

    const interval = window.setInterval(() => {
      const timeoutMinutes = security?.session_timeout_minutes || 480;
      const timeoutMs = timeoutMinutes * 60 * 1000;
      const remainingMs = Math.max(0, lastActivityRef.current + timeoutMs - Date.now());
      const remainingSeconds = Math.ceil(remainingMs / 1000);

      if (remainingSeconds <= 0) {
        logout();
      } else if (remainingSeconds <= 60) {
        setSecondsRemaining(remainingSeconds);
        setSessionWarningOpen(true);
      }
    }, 1000);

    return () => {
      activityEvents.forEach(eventName => window.removeEventListener(eventName, markActivity));
      window.clearInterval(interval);
    };
  }, [isAuthenticated, logout, recordSecurityActivity, security?.session_timeout_minutes]);

  const handleNav = useCallback((id) => {
    if (id === screen) {
      setMobileNavOpen(false);
      return;
    }
    setScreen(id);
    setScreenHistory(prev => {
      if (prev[prev.length - 1] === id) return prev;
      return [...prev, id];
    });
    setMobileNavOpen(false);
  }, [screen]);

  const handleBack = useCallback(() => {
    // 1. If mobile navigation drawer is open, close it
    if (mobileNavOpen) {
      setMobileNavOpen(false);
      return true;
    }

    // 2. If unauthenticated
    if (!isAuthenticated) {
      if (authState !== 'login') {
        setAuthState('login');
        return true;
      }
      handleRootExit(translations[language]?.common?.msg_press_back_again_to_exit || "Press back again to exit");
      return true;
    }

    // 3. If authenticated
    if (screenHistory.length > 1) {
      const nextHistory = [...screenHistory];
      nextHistory.pop(); // Remove current screen
      const prevScreen = nextHistory[nextHistory.length - 1];
      setScreenHistory(nextHistory);
      setScreen(prevScreen);
      return true;
    } else if (screen !== rootScreen) {
      setScreen(rootScreen);
      setScreenHistory([rootScreen]);
      return true;
    } else {
      // Already on the root screen (e.g. Dashboard or Maintenance)
      handleRootExit(translations[language]?.common?.msg_press_back_again_to_exit || "Press back again to exit");
      return true;
    }
  }, [mobileNavOpen, isAuthenticated, authState, screenHistory, screen, rootScreen, language]);

  const handleBackRef = useRef(handleBack);
  useEffect(() => {
    handleBackRef.current = handleBack;
  }, [handleBack]);

  // Register hardware back button listener for Capacitor native Android / iOS
  useEffect(() => {
    initBackButtonManager(() => {
      return handleBackRef.current?.();
    });
  }, []);

  const handleRefresh = (event) => {
    setRefreshKey(prev => prev + 1);
    if (event?.detail?.complete) {
      try {
        event.detail.complete();
      } catch (e) {
        console.error(e);
      }
    }
  };

  if (window.location.pathname.startsWith('/shared-doc/')) {
    return <SharedDocumentViewer />;
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface dark:bg-slate-950 transition-colors">
        <div className="w-10 h-10 border-[3px] border-slate-200 dark:border-slate-800 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authState === "signup") return <SignupScreen onLogin={(email) => {
      if (email && typeof email === 'string') setRegisteredEmail(email);
      setAuthState("login");
    }} />;
    if (authState === "forgot") return <ForgotPasswordScreen onBack={() => setAuthState("login")} />;
    if (authState === "impersonate") return <ImpersonateScreen />;
    return <LoginScreen initialEmail={registeredEmail} onLogin={() => setAuthState("app")} onSignup={() => setAuthState("signup")} onForgot={() => setAuthState("forgot")} />;
  }

  const { title, C } = SCREENS[screen] || SCREENS.dashboard;

  
  // Find if this screen requires a feature entitlement
  let requiredFeature = null;
  for (const group of NAV) {
    const item = group.items.find(i => i.id === screen);
    if (item && item.feature) {
      requiredFeature = item.feature;
      break;
    }
  }

  return (
    <IonApp>
      <IonPage>
        <div className="flex h-screen overflow-hidden bg-surface dark:bg-slate-950 transition-colors" style={{ fontFamily: "'Inter',system-ui,sans-serif" }}>
          <Sidebar
            active={screen}
            onNav={handleNav}
            collapsed={collapsed}
            onLogout={() => setLogoutModal(true)}
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
            user={user}
          />
          <div className="flex flex-col flex-1 overflow-hidden min-w-0">
            <Header
              title={title}
              onToggle={() => setCollapsed(c => !c)}
              onNav={handleNav}
              onLogout={() => setLogoutModal(true)}
              onMobileMenuOpen={() => setMobileNavOpen(true)}
              onRefresh={() => handleRefresh()}
              user={user}
              canGoBack={screenHistory.length > 1 || screen !== rootScreen}
              onBack={handleBack}
            />
            <div className="flex-1 relative overflow-hidden">
              <IonContent className="bg-surface">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                  <IonRefresherContent />
                </IonRefresher>
                <div className="p-3 sm:p-4 lg:p-5 pb-28 sm:pb-8" style={{ paddingBottom: 'calc(6.5rem + env(safe-area-inset-bottom, 24px))' }}>
                  {requiredFeature ? (
                    <FeatureGate feature={requiredFeature}>
                      <C onNav={handleNav} key={screen + refreshKey} />
                    </FeatureGate>
                  ) : (
                    <C onNav={handleNav} key={screen + refreshKey} />
                  )}
                </div>
              </IonContent>
            </div>
          </div>
        </div>
        {logoutModal && (
          <LogoutModal
            onConfirm={() => {
              setLogoutModal(false);
              logout();
            }}
            onCancel={() => setLogoutModal(false)}
          />
        )}
        {sessionWarningOpen && (
          <Modal title="Session expiring soon" onClose={() => setSessionWarningOpen(false)}>
            <div className="space-y-4">
              <p className="text-sm text-muted">
                Your session will expire in about {Math.max(1, Math.ceil((secondsRemaining || 60) / 60))} minute{Math.ceil((secondsRemaining || 60) / 60) > 1 ? 's' : ''} due to inactivity.
              </p>
              <div className="flex flex-wrap gap-3 justify-end">
                <button
                  onClick={async () => {
                    await recordSecurityActivity({ activity_at: new Date().toISOString() });
                    lastActivityRef.current = Date.now();
                    setSessionWarningOpen(false);
                  }}
                  className="px-4 py-2 rounded-lg bg-primary text-white font-semibold"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={logout}
                  className="px-4 py-2 rounded-lg border border-border text-ink font-semibold bg-white dark:bg-slate-900"
                >
                  Logout Now
                </button>
              </div>
            </div>
          </Modal>
        )}
      </IonPage>
    </IonApp>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
      {createPortal(<Toaster position="top-center" containerStyle={{ zIndex: 999999 }} />, document.body)}
    </AuthProvider>
  );
}
